import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check for existing session on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (token && storedUser) {
        try {
          // Verify token is still valid
          const response = await authAPI.getMe();
          setUser(response.data.data);
        } catch (err) {
          // Token invalid, clear storage
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await authAPI.login({ email, password });
      
      if (response.data.success) {
        const { token, user: userData } = response.data.data;
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        return { success: true };
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Login failed. Please try again.';
      setError(message);
      return { success: false, message };
    }
  };

  const studentLogin = async (studentId, email) => {
    try {
      setError(null);
      const response = await authAPI.studentLogin({ studentId, email });
      
      if (response.data.success) {
        // Code sent successfully - return data for the UI to move to verification step
        return { 
          success: true, 
          codeSent: true,
          studentId: response.data.data.studentId,
          email: response.data.data.email,
        };
      } else {
        const message = response.data.message || 'Login failed. Please check your credentials.';
        setError(message);
        return { success: false, message };
      }
    } catch (err) {
      console.error('Student login error:', err);
      const message = err.response?.data?.message || 'Login failed. Please check your Student ID and Email.';
      setError(message);
      return { success: false, message };
    }
  };

  const studentVerify = async (studentId, email, verificationCode) => {
    try {
      setError(null);
      const response = await authAPI.studentVerify({ studentId, email, verificationCode });
      
      if (response.data.success) {
        const { token, user: userData } = response.data.data;
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        return { success: true };
      } else {
        const message = response.data.message || 'Verification failed. Please check your code.';
        setError(message);
        return { success: false, message };
      }
    } catch (err) {
      console.error('Student verification error:', err);
      const message = err.response?.data?.message || 'Verification failed. Please check your code and try again.';
      setError(message);
      return { success: false, message };
    }
  };

  const resendVerificationCode = async (studentId, email) => {
    try {
      setError(null);
      const response = await authAPI.resendCode({ studentId, email });
      
      if (response.data.success) {
        return { success: true, message: response.data.message };
      } else {
        const message = response.data.message || 'Failed to resend code.';
        setError(message);
        return { success: false, message };
      }
    } catch (err) {
      console.error('Resend code error:', err);
      const message = err.response?.data?.message || 'Failed to resend code. Please try again.';
      setError(message);
      return { success: false, message };
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      // Ignore logout errors
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
    }
  };

  // Refresh user data from server (useful after profile updates)
  const refreshUser = async () => {
    try {
      const response = await authAPI.getMe();
      if (response.data.success) {
        setUser(response.data.data);
        localStorage.setItem('user', JSON.stringify(response.data.data));
        return { success: true };
      }
    } catch (err) {
      console.error('Failed to refresh user:', err);
      return { success: false };
    }
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    studentLogin,
    studentVerify,
    resendVerificationCode,
    logout,
    setError,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
